import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import EmptyState from '../../../components/EmptyState/EmptyState';
import Pagination from '../../../components/Pagination/Pagination';
import {
    deleteStrategy,
    getStrategies,
    startStrategy,
    stopStrategy,
} from '../../../services/StrategiesService';
import { confirmDelete } from '../../../utils/confirmDialog';
import StrategyModal from '../StrategyModal/StrategyModal';
import MyStrategyRow from './MyStrategyRow';

/**
 * props:
 * - symbol
 * - onNotification
 * - canCreate (plan flag: hides the create action when the plan lacks Strategies)
 */
function MyStrategies(props) {
    const defaultLocation = useLocation();

    function getPage(location) {
        if (!location) location = defaultLocation;
        return new URLSearchParams(location.search).get('page');
    }

    useEffect(() => {
        setPage(getPage(defaultLocation));
    }, [defaultLocation]);

    const [strategies, setStrategies] = useState([]);

    const [count, setCount] = useState(0);

    const [editStrategy, setEditStrategy] = useState({});

    const [page, setPage] = useState(getPage());

    useEffect(() => {
        getStrategies(props.symbol, page || 1)
            .then(result => {
                setStrategies(result.rows);
                setCount(result.count);
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                props.onNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }, [page, props.symbol]);

    function onEditClick(event) {
        const id = event.target.id.replace('edit', '');
        const strategy = strategies.find(s => s.id == id);
        setEditStrategy({ ...strategy });
    }

    function onStopClick(event) {
        const id = event.target.id.replace('stop', '');
        stopStrategy(id)
            .then(strategy => {
                window.location.reload();
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                props.onNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onStartClick(event) {
        const id = event.target.id.replace('start', '');
        startStrategy(id)
            .then(strategy => {
                window.location.reload();
            })
            .catch(err => {
                console.error(err.response ? err.response.data : err.message);
                props.onNotification({
                    type: 'error',
                    text: err.response ? err.response.data : err.message,
                });
            });
    }

    function onDeleteClick(event) {
        const id = event.target.id.replace('delete', '');
        confirmDelete('This strategy will be permanently deleted!').then(
            confirmed => {
                if (!confirmed) return;
                deleteStrategy(id)
                    .then(strategy => {
                        window.location.reload();
                    })
                    .catch(err => {
                        console.error(
                            err.response ? err.response.data : err.message,
                        );
                        props.onNotification({
                            type: 'error',
                            text: err.response
                                ? err.response.data
                                : err.message,
                        });
                    });
            },
        );
    }

    function onStrategySubmit(strategy) {
        window.location.reload();
    }

    return (
        <>
            <div className="card card-body border-0 shadow table-wrapper table-responsive">
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th className="border-gray-200">Symbol</th>
                            <th className="border-gray-200">Name</th>
                            <th className="border-gray-200">Status</th>
                            <th className="border-gray-200">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {strategies && strategies.length ? (
                            strategies.map(strategy => (
                                <MyStrategyRow
                                    key={strategy.id}
                                    data={strategy}
                                    onEditClick={onEditClick}
                                    onStartClick={onStartClick}
                                    onStopClick={onStopClick}
                                    onDeleteClick={onDeleteClick}
                                />
                            ))
                        ) : (
                            <EmptyState
                                colSpan={4}
                                title="No strategies yet"
                                description="Create your first strategy to group conditions and actions you can reuse."
                                actionLabel="Create your first strategy"
                                canCreate={props.canCreate !== false}
                                actionAttrs={{
                                    'data-bs-toggle': 'modal',
                                    'data-bs-target': '#modalStrategy',
                                }}
                            />
                        )}
                    </tbody>
                </table>
                <Pagination count={count} />
            </div>
            <StrategyModal data={editStrategy} onSubmit={onStrategySubmit} />
        </>
    );
}

export default MyStrategies;
